import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Search, Building2, MapPin, Users, Filter, X,
  Globe, Linkedin, Phone, Mail, UserPlus, Sparkles,
  Loader2, Eye, Lock, Zap, CreditCard, Bookmark, BookmarkCheck,
  Trash2, Play, Clock, Briefcase, UserCheck, Code,
  ChevronDown, ChevronUp, DollarSign, Cpu, TrendingUp,
  ArrowRight, ExternalLink, Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import { AutoProspectToggle } from '../components/leads/AutoProspectToggle';
import { AutonomyControls } from '../components/leads/AutonomyControls';
import { GoAutonomousCard } from '../components/leads/GoAutonomousCard';
import { PauseAiButton } from '../components/leads/PauseAiButton';
import { SnoozedLeadsCard } from '../components/leads/SnoozedLeadsCard';

const INDUSTRY_OPTIONS = [
  'information technology & services', 'computer software', 'internet',
  'financial services', 'marketing and advertising', 'hospital & health care',
  'management consulting', 'real estate', 'education management',
  'retail', 'telecommunications', 'automotive', 'food & beverages',
  'construction', 'logistics and supply chain', 'manufacturing',
  'industrial automation', 'oil & energy', 'banking', 'insurance',
  'staffing and recruiting', 'mechanical or industrial engineering',
];

const EMPLOYEE_RANGES = [
  { label: '1-10', value: '1,10' },
  { label: '11-50', value: '11,50' },
  { label: '51-200', value: '51,200' },
  { label: '201-500', value: '201,500' },
  { label: '501-1K', value: '501,1000' },
  { label: '1K-5K', value: '1001,5000' },
  { label: '5K-10K', value: '5001,10000' },
  { label: '10K+', value: '10001,100000' },
];

const SENIORITY_LABELS = {
  c_suite: 'C-Suite', founder: 'Founder', vp: 'VP', director: 'Director',
  manager: 'Manager', senior: 'Senior', entry: 'Entry',
};

const REVENUE_RANGES = [
  { label: '<$1M', value: '0,1000000' },
  { label: '$1M-$10M', value: '1000000,10000000' },
  { label: '$10M-$50M', value: '10000000,50000000' },
  { label: '$50M-$100M', value: '50000000,100000000' },
  { label: '$100M-$500M', value: '100000000,500000000' },
  { label: '$500M-$1B', value: '500000000,1000000000' },
  { label: '$1B+', value: '1000000000,' },
];

const FUNDING_STAGES = [
  { label: 'Seed', value: 'seed' },
  { label: 'Series A', value: 'series_a' },
  { label: 'Series B', value: 'series_b' },
  { label: 'Series C', value: 'series_c' },
  { label: 'Series D+', value: 'series_d' },
  { label: 'IPO', value: 'ipo' },
  { label: 'PE', value: 'private_equity' },
];

const FRESHNESS_OPTIONS = [
  { label: 'Any time', value: '' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' },
  { label: 'Last 6 months', value: '180' },
  { label: 'Last year', value: '365' },
];

/* ─── Tiny Components ─── */

function FilterChip({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-violet-500/10 text-violet-400 border border-violet-500/20">
      {label}
      <X className="h-2.5 w-2.5 cursor-pointer hover:text-red-400 transition-colors" onClick={onRemove} />
    </span>
  );
}

function StatPill({ icon: Icon, label, className = '' }) {
  if (!label || label === '—') return null;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium ${className}`}>
      <Icon className="h-2.5 w-2.5" />
      {label}
    </span>
  );
}

function TechBadge({ name }) {
  return (
    <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/15">
      {name}
    </span>
  );
}

function ICPScoreBadge({ score, label }) {
  if (!score && score !== 0) return null;
  const styles = {
    hot: 'bg-red-500/15 text-red-400 border-red-500/25',
    warm: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
    cold: 'bg-blue-500/15 text-blue-400 border-blue-500/25',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${styles[label] || styles.cold}`} data-testid="icp-score-badge">
      <Sparkles className="h-2.5 w-2.5" />
      {score}% {label?.toUpperCase()}
    </span>
  );
}


/* ─── Company Card ─── */

function CompanyCard({ company, expanded, onExpand, onImport, importingId }) {
  const loc = [company.city, company.state, company.country].filter(Boolean).join(', ');
  const desc = (typeof company.description === 'string' && company.description.length > 0) ? company.description : null;
  const techs = (company.technologies || []).slice(0, 5);
  const keywords = (company.keywords || []).slice(0, 4);
  const importKey = company.apollo_id;
  const isImporting = importingId === importKey;

  const formatRevenue = (rev) => {
    if (!rev) return null;
    if (typeof rev === 'string') return rev;
    if (rev >= 1e9) return `$${(rev / 1e9).toFixed(1)}B`;
    if (rev >= 1e6) return `$${(rev / 1e6).toFixed(0)}M`;
    if (rev >= 1e3) return `$${(rev / 1e3).toFixed(0)}K`;
    return `$${rev}`;
  };

  const formatFunding = (val) => {
    if (!val) return null;
    if (val >= 1e9) return `$${(val / 1e9).toFixed(1)}B`;
    if (val >= 1e6) return `$${(val / 1e6).toFixed(0)}M`;
    if (val >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
    return `$${val}`;
  };

  const fundingStageLabel = (stage) => {
    if (!stage) return null;
    const map = { seed: 'Seed', series_a: 'Series A', series_b: 'Series B', series_c: 'Series C', series_d: 'Series D', ipo: 'IPO', private_equity: 'PE', debt_financing: 'Debt' };
    return map[stage] || stage.replace(/_/g, ' ');
  };

  return (
    <div
      className={`group rounded-xl border transition-all duration-200 ${
        expanded
          ? 'border-violet-500/30 bg-violet-500/[0.03] shadow-lg shadow-violet-500/5'
          : 'border-border/60 bg-card hover:border-border hover:shadow-md hover:shadow-black/5'
      }`}
      data-testid={`company-card-${company.apollo_id}`}
    >
      {/* Main Card Content */}
      <div className="px-4 py-3.5 cursor-pointer" onClick={() => onExpand(company)}>
        <div className="flex items-start gap-3.5">
          {/* Logo */}
          <div className="w-10 h-10 rounded-lg bg-muted/60 flex items-center justify-center shrink-0 overflow-hidden border border-border/40">
            {company.logo_url ? (
              <img src={company.logo_url} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
            ) : null}
            <Building2 className="h-4 w-4 text-muted-foreground" style={company.logo_url ? { display: 'none' } : {}} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Row 1: Name + Domain + Quick Actions */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="text-sm font-semibold text-foreground truncate">{company.name}</h3>
                {company.domain && (
                  <span className="text-[10px] text-muted-foreground/70 truncate hidden sm:inline">{company.domain}</span>
                )}
                <ICPScoreBadge score={company.icp_score} label={company.icp_label} />
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {company.linkedin_url && (
                  <a href={company.linkedin_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="p-1 rounded hover:bg-muted transition-colors">
                    <Linkedin className="h-3 w-3 text-muted-foreground hover:text-blue-400" />
                  </a>
                )}
                {company.website_url && (
                  <a href={company.website_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="p-1 rounded hover:bg-muted transition-colors">
                    <ExternalLink className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                  </a>
                )}
                <Button
                  size="sm" variant="ghost"
                  className="h-7 px-2.5 text-[11px] font-medium text-violet-400 hover:text-violet-300 hover:bg-violet-500/10 transition-all disabled:opacity-100"
                  disabled={isImporting}
                  onClick={e => { e.stopPropagation(); onImport(company, null); }}
                  data-testid={`add-pipeline-btn-${company.apollo_id}`}
                >
                  {isImporting
                    ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Adding...</>
                    : <><UserPlus className="h-3 w-3 mr-1" />Add to Pipeline</>}
                </Button>
              </div>
            </div>

            {/* Row 2: Key Metrics Pills */}
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {company.industry && (
                <StatPill icon={Briefcase} label={company.industry} className="bg-foreground/5 text-foreground/60" />
              )}
              {company.employee_count && (
                <StatPill icon={Users} label={`${company.employee_count.toLocaleString()} emp`} className="bg-foreground/5 text-foreground/60" />
              )}
              {formatRevenue(company.annual_revenue) && (
                <StatPill icon={DollarSign} label={formatRevenue(company.annual_revenue)} className="bg-emerald-500/10 text-emerald-400" />
              )}
              {formatFunding(company.total_funding) && (
                <StatPill icon={TrendingUp} label={formatFunding(company.total_funding)} className="bg-amber-500/10 text-amber-400" />
              )}
              {fundingStageLabel(company.latest_funding_stage) && (
                <StatPill icon={Layers} label={fundingStageLabel(company.latest_funding_stage)} className="bg-blue-500/10 text-blue-400" />
              )}
              {loc && (
                <StatPill icon={MapPin} label={loc} className="bg-foreground/5 text-foreground/50" />
              )}
              {company.founded_year && (
                <span className="text-[10px] text-muted-foreground/50">Est. {company.founded_year}</span>
              )}
            </div>

            {/* Row 3: Description */}
            {desc && (
              <p className="text-[11px] text-muted-foreground/70 mt-1.5 line-clamp-1 leading-relaxed">{desc}</p>
            )}

            {/* Row 4: Tech Stack + Keywords */}
            {(techs.length > 0 || keywords.length > 0) && (
              <div className="flex items-center gap-1 mt-2 flex-wrap">
                {techs.length > 0 && <Cpu className="h-2.5 w-2.5 text-cyan-500/50 mr-0.5" />}
                {techs.map((t, i) => <TechBadge key={i} name={t} />)}
                {techs.length > 0 && keywords.length > 0 && <span className="text-border mx-0.5">|</span>}
                {keywords.map((kw, i) => (
                  <span key={i} className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-foreground/5 text-foreground/40">{kw}</span>
                ))}
              </div>
            )}
          </div>

          {/* Expand Indicator */}
          <div className="pt-1 shrink-0">
            {expanded ? <ChevronUp className="h-4 w-4 text-violet-400" /> : <ChevronDown className="h-4 w-4 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />}
          </div>
        </div>
      </div>
    </div>
  );
}


/* ─── Expanded Company Detail — People Panel ─── */

function CompanyPeoplePanel({ company, people, loadingPeople, onReveal, revealingId, onImport, importingId, credits, seniorityFilter }) {
  return (
    <div className="px-4 pb-4 pt-0" data-testid="company-detail-panel">
      <div className="rounded-lg border border-border/50 bg-background/50 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-muted/20 border-b border-border/30">
          <h4 className="text-xs font-semibold text-foreground flex items-center gap-2">
            <Users className="h-3.5 w-3.5 text-violet-400" />
            People at {company.name}
            {seniorityFilter && (
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 bg-amber-500/10 text-amber-400 border-amber-500/20">
                {SENIORITY_LABELS[seniorityFilter] || seniorityFilter}
              </Badge>
            )}
          </h4>
          <span className="text-[10px] text-muted-foreground">
            Reveal: ${credits?.reveal_cost?.toFixed(3) || '0.065'}/contact
          </span>
        </div>

        {/* People List */}
        <div className="p-2">
          {loadingPeople ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-4 w-4 animate-spin text-violet-400" />
              <span className="ml-2 text-xs text-muted-foreground">Finding contacts...</span>
            </div>
          ) : people.length === 0 ? (
            <div className="text-center py-8 text-xs text-muted-foreground">No contacts found at this company.</div>
          ) : (
            <div className="space-y-1 max-h-[320px] overflow-y-auto">
              {people.map((person, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/30 transition-colors group/person" data-testid={`person-row-${i}`}>
                  {/* Avatar */}
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500/80 to-indigo-600/80 flex items-center justify-center text-white text-[11px] font-bold shrink-0">
                    {(person.first_name?.[0] || person.name?.[0] || '?').toUpperCase()}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold text-foreground truncate">{person.name}</span>
                      {person.seniority && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 border-foreground/10">{SENIORITY_LABELS[person.seniority] || person.seniority}</Badge>
                      )}
                      {person.departments?.length > 0 && (
                        <span className="text-[9px] text-muted-foreground/60 hidden sm:inline">{person.departments[0]}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-[10px] text-muted-foreground truncate">{person.title}</p>
                      {(person.city || person.country) && (
                        <span className="text-[9px] text-muted-foreground/50 flex items-center gap-0.5 shrink-0">
                          <MapPin className="h-2 w-2" />{[person.city, person.country].filter(Boolean).join(', ')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Contact + Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    {person.email ? (
                      <span className="flex items-center gap-1 text-[10px] text-emerald-400"><Mail className="h-3 w-3" />{person.email}</span>
                    ) : person.has_email ? (
                      <span className="flex items-center gap-1 text-[10px] text-muted-foreground/60"><Lock className="h-2.5 w-2.5" />Email</span>
                    ) : null}
                    {person.phone ? (
                      <span className="flex items-center gap-1 text-[10px] text-emerald-400"><Phone className="h-3 w-3" />{person.phone}</span>
                    ) : person.has_phone ? (
                      <span className="flex items-center gap-1 text-[10px] text-muted-foreground/60"><Lock className="h-2.5 w-2.5" />Phone</span>
                    ) : null}
                    {person.linkedin_url && (
                      <a href={person.linkedin_url} target="_blank" rel="noopener noreferrer"><Linkedin className="h-3 w-3 text-muted-foreground hover:text-blue-400 transition-colors" /></a>
                    )}
                    {!person.email && person.has_email && (
                      <Button
                        size="sm" variant="ghost"
                        className="h-6 text-[10px] text-violet-400 hover:bg-violet-500/10"
                        disabled={revealingId === person.apollo_id}
                        onClick={() => onReveal(person, false)}
                        data-testid={`reveal-btn-${i}`}
                        title="Reveal work email (1 credit)"
                      >
                        {revealingId === person.apollo_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Eye className="h-3 w-3 mr-0.5" />Reveal email</>}
                      </Button>
                    )}
                    {!person.phone && person.has_phone && (
                      <Button
                        size="sm" variant="ghost"
                        className="h-6 text-[10px] text-amber-400 hover:bg-amber-500/10"
                        disabled={revealingId === `${person.apollo_id}-mobile`}
                        onClick={() => onReveal(person, true)}
                        data-testid={`reveal-mobile-btn-${i}`}
                        title="Reveal mobile number — premium (~8 credits, arrives shortly)"
                      >
                        {revealingId === `${person.apollo_id}-mobile` ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Phone className="h-3 w-3 mr-0.5" />Reveal mobile</>}
                      </Button>
                    )}
                    <Button
                      size="sm" variant="ghost"
                      className="h-6 px-1.5 text-[10px] text-foreground/40 hover:text-violet-400 hover:bg-violet-500/10 opacity-0 group-hover/person:opacity-100 transition-all"
                      onClick={() => onImport(null, person)}
                      data-testid={`import-person-btn-${i}`}
                    >
                      <UserPlus className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/* ─── Saved Search Panel ─── */

function SavedSearchPanel({ items, onLoad, onDelete, onRun, runningId }) {
  if (!items.length) return (
    <div className="text-center py-6 text-xs text-muted-foreground">No saved searches yet.</div>
  );
  return (
    <div className="space-y-1" data-testid="saved-searches-list">
      {items.map(s => (
        <div key={s.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border/40 bg-card hover:bg-muted/20 transition-colors group">
          <BookmarkCheck className="h-3.5 w-3.5 text-violet-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-foreground truncate">{s.name}</span>
              {s.last_result_count > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-500/10 text-violet-400">{s.last_result_count.toLocaleString()}</span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              {s.filters?.industries?.map((ind, i) => (
                <span key={i} className="text-[9px] text-muted-foreground">{ind}</span>
              ))}
              {s.last_run_at && (
                <span className="text-[9px] text-muted-foreground flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" />{new Date(s.last_run_at).toLocaleDateString()}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => onLoad(s)} data-testid={`load-search-${s.id}`}><Search className="h-3 w-3" /></Button>
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => onRun(s.id)} disabled={runningId === s.id} data-testid={`run-search-${s.id}`}>
              {runningId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
            </Button>
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-300" onClick={() => onDelete(s.id)} data-testid={`delete-search-${s.id}`}><Trash2 className="h-3 w-3" /></Button>
          </div>
        </div>
      ))}
    </div>
  );
}


/* ═════════════════════════════════════
   MAIN: LeadSearch Page
   ═════════════════════════════════════ */

export default function LeadSearch() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Search state
  const [searchName, setSearchName] = useState('');
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedEmployeeRanges, setSelectedEmployeeRanges] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [aiParsing, setAiParsing] = useState(false);

  // Results state
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [providerError, setProviderError] = useState(null);

  // Detail state
  const [expandedId, setExpandedId] = useState(null);
  const [companyPeople, setCompanyPeople] = useState({});
  const [loadingPeopleFor, setLoadingPeopleFor] = useState(null);

  // Reveal & Import state
  const [revealingId, setRevealingId] = useState(null);
  const [importingId, setImportingId] = useState(null);

  // Credits
  const [credits, setCredits] = useState({ balance: 0, enrich_cost: 0.01, reveal_cost: 0.065 });

  // Seniority filter
  const [seniorityFilter, setSeniorityFilter] = useState('');

  // Revenue / funding / freshness filters
  const [selectedRevenue, setSelectedRevenue] = useState('');
  const [selectedFundingStages, setSelectedFundingStages] = useState([]);
  const [dataFreshness, setDataFreshness] = useState('');

  // Suggestions
  const [suggestions, setSuggestions] = useState(null);

  // Saved Searches
  const [savedSearches, setSavedSearches] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [runningSavedId, setRunningSavedId] = useState(null);

  useEffect(() => {
    loadSuggestions();
    loadSavedSearches();
    loadCredits();
  }, []);

  // Handle URL params from dashboard shortcuts
  const urlParamsProcessed = React.useRef(false);
  useEffect(() => {
    if (urlParamsProcessed.current) return;
    const seniorityParam = searchParams.get('seniority');
    const industryParam = searchParams.get('industry');
    const titleParam = searchParams.get('title');
    const locationParam = searchParams.get('location');
    if (!seniorityParam && !industryParam && !titleParam && !locationParam) return;
    urlParamsProcessed.current = true;

    const body = { page: 1, per_page: 25 };
    // Defensive: strip descriptive parentheticals from URL-param values before
    // they hit the API body. Chip labels keep the descriptive form via the
    // separate setSelectedIndustries/Locations calls below.
    const stripParens = (s) => (s || '').replace(/\s*\([^)]*\)/g, '').trim();
    if (seniorityParam) setSeniorityFilter(seniorityParam);
    if (industryParam) {
      setSelectedIndustries([industryParam]);
      const clean = stripParens(industryParam);
      if (clean) body.industries = [clean];
    }
    if (titleParam) {
      // Map persona title → Apollo seniority (people attribute) so the people
      // expansion inside each company is pre-filtered to the right buyer.
      const tl = titleParam.toLowerCase();
      const seniority = /chief|ceo|coo|cfo|cto|cmo|founder|owner|president/.test(tl) ? 'c_suite'
        : /vp|vice president/.test(tl) ? 'vp'
        : /director/.test(tl) ? 'director'
        : /head|manager/.test(tl) ? 'manager' : null;
      if (seniority) setSeniorityFilter(seniority);
      if (!industryParam && !locationParam) {
        // No company-level filter available — best-effort keyword search
        setKeywords([titleParam]);
        body.keywords = [titleParam];
      }
    }
    if (locationParam) {
      setSelectedLocations([locationParam]);
      const clean = stripParens(locationParam);
      if (clean) body.locations = [clean];
    }

    setLoading(true);
    setHasSearched(true);
    api.post('/api/leads/search/companies', body).then(res => {
      if (res.data.error && res.data.message) setProviderError(res.data.message);
      setResults(res.data.organizations || []);
      setTotal(res.data.total || 0);
      setPage(1);
      if (res.data.credits) setCredits(res.data.credits);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [searchParams]);

  const loadCredits = async () => {
    try {
      const res = await api.get('/api/tenant/billing/credits/summary');
      if (res.data) setCredits(prev => ({ ...prev, balance: res.data.balance ?? res.data.credit_balance_usd ?? 0 }));
    } catch { /* ignore */ }
  };

  const loadSuggestions = async () => {
    try {
      const res = await api.get('/api/leads/search/suggestions');
      setSuggestions(res.data);
      const hasUrlIndustry = searchParams.get('industry');
      if (!hasUrlIndustry && res.data.industries?.length) setSelectedIndustries(res.data.industries.slice(0, 2));
      if (res.data.employee_ranges?.length) setSelectedEmployeeRanges(res.data.employee_ranges);
    } catch { /* ignore */ }
  };

  const loadSavedSearches = async () => {
    try {
      const res = await api.get('/api/leads/search/saved-searches');
      setSavedSearches(res.data.items || []);
    } catch { /* ignore */ }
  };

  const getCurrentFilters = () => {
    // Defensive normalization: strip descriptive parentheticals from
    // industry/location strings before they hit the backend. The backend
    // already normalizes via services.apollo_taxonomy, but sanitizing here
    // too keeps the outbound body clean regardless of the entry point
    // (URL params, chip state, saved-search restore, brain seed).
    // Chip labels themselves keep the descriptive form for readability.
    const stripParens = (s) => (s || '').replace(/\s*\([^)]*\)/g, '').trim();
    const cleanList = (arr) => (arr || []).map(stripParens).filter(Boolean);
    const filters = {};
    if (searchName.trim()) filters.name = searchName.trim();
    if (selectedIndustries.length) filters.industries = cleanList(selectedIndustries);
    if (selectedLocations.length) filters.locations = cleanList(selectedLocations);
    if (selectedEmployeeRanges.length) filters.employee_ranges = selectedEmployeeRanges;
    if (keywords.length) filters.keywords = keywords;
    if (selectedRevenue) {
      const [rmin, rmax] = selectedRevenue.split(',');
      if (rmin) filters.revenue_min = parseInt(rmin, 10);
      if (rmax) filters.revenue_max = parseInt(rmax, 10);
    }
    if (selectedFundingStages.length) filters.funding_stages = selectedFundingStages;
    if (dataFreshness) filters.data_fresh_days = parseInt(dataFreshness, 10);
    return filters;
  };

  const handleSaveSearch = async () => {
    if (!saveName.trim()) { toast.error('Enter a name'); return; }
    const filters = getCurrentFilters();
    if (!Object.keys(filters).length) { toast.error('Add filters first'); return; }
    try {
      const res = await api.post('/api/leads/search/saved-searches', { name: saveName.trim(), filters });
      setSavedSearches(prev => [res.data.saved_search, ...prev]);
      setSaveName('');
      setShowSaveInput(false);
      toast.success('Search saved');
    } catch { toast.error('Save failed'); }
  };

  const handleDeleteSavedSearch = async (id) => {
    try {
      await api.delete(`/api/leads/search/saved-searches/${id}`);
      setSavedSearches(prev => prev.filter(s => s.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const handleLoadSavedSearch = (saved) => {
    const f = saved.filters || {};
    setSearchName(f.name || '');
    setSelectedIndustries(f.industries || []);
    setSelectedLocations(f.locations || []);
    setSelectedEmployeeRanges(f.employee_ranges || []);
    setKeywords(f.keywords || []);
    setSelectedFundingStages(f.funding_stages || []);
    setDataFreshness(f.data_fresh_days ? String(f.data_fresh_days) : '');
    setSelectedRevenue(
      (f.revenue_min != null || f.revenue_max != null)
        ? `${f.revenue_min ?? ''},${f.revenue_max ?? ''}`
        : ''
    );
    setShowSaved(false);
    toast.success(`Loaded: ${saved.name}`);
  };

  const handleRunSavedSearch = async (id) => {
    setRunningSavedId(id);
    try {
      const res = await api.post(`/api/leads/search/saved-searches/${id}/run`);
      setResults(res.data.organizations || []);
      setTotal(res.data.total || 0);
      setPage(1);
      setHasSearched(true);
      setExpandedId(null);
      setShowSaved(false);
      loadSavedSearches();
      toast.success(`Found ${res.data.total?.toLocaleString()} companies`);
    } catch { toast.error('Run failed'); }
    finally { setRunningSavedId(null); }
  };

  // Detect if input looks like natural language (vs simple keyword/name)
  const isNaturalLanguage = (text) => {
    const trimmed = text.trim();
    if (!trimmed || trimmed.length < 10) return false;
    const words = trimmed.split(/\s+/);
    if (words.length < 3) return false;
    // If it contains action words or descriptors, it's likely NL
    const nlSignals = ['find', 'show', 'search', 'looking', 'want', 'need', 'companies', 'startups', 'founders', 'with', 'in', 'at', 'that', 'who', 'employees', 'revenue', 'based', 'located', 'tech', 'saas', 'enterprise', 'small', 'large', 'medium'];
    return words.some(w => nlSignals.includes(w.toLowerCase()));
  };

  const handleAISearch = async () => {
    const query = searchName.trim();

    // If no query text, use regular filter-based search
    if (!query) {
      handleSearch(1);
      return;
    }

    if (!isNaturalLanguage(query)) {
      // Plain keyword/name search — direct
      handleSearch(1);
      return;
    }

    // AI parsing
    setAiParsing(true);
    try {
      const res = await api.post('/api/leads/search/parse-query', { query });
      const filters = res.data?.filters || {};

      // Populate filter state from AI response
      if (filters.name) setSearchName(filters.name);
      if (filters.industries?.length) setSelectedIndustries(prev => [...new Set([...prev, ...filters.industries])]);
      if (filters.locations?.length) setSelectedLocations(prev => [...new Set([...prev, ...filters.locations])]);
      if (filters.employee_ranges?.length) setSelectedEmployeeRanges(prev => [...new Set([...prev, ...filters.employee_ranges])]);
      if (filters.keywords?.length) setKeywords(prev => [...new Set([...prev, ...filters.keywords])]);
      if (filters.seniority) setSeniorityFilter(filters.seniority);

      if (res.data.parsed) {
        toast.success('Brain parsed your query into filters', { duration: 2000 });
      }

      // Now run the search with all filters
      setLoading(true);
      setHasSearched(true);
      const searchBody = {
        ...getCurrentFilters(),
        ...(filters.name ? { name: filters.name } : {}),
        ...(filters.industries?.length ? { industries: filters.industries } : {}),
        ...(filters.locations?.length ? { locations: filters.locations } : {}),
        ...(filters.employee_ranges?.length ? { employee_ranges: filters.employee_ranges } : {}),
        ...(filters.keywords?.length ? { keywords: filters.keywords } : {}),
        page: 1,
        per_page: 25,
      };
      // Clean empty arrays
      Object.keys(searchBody).forEach(k => { if (Array.isArray(searchBody[k]) && !searchBody[k].length) delete searchBody[k]; });

      const searchRes = await api.post('/api/leads/search/companies', searchBody);
      setResults(searchRes.data.organizations || []);
      setTotal(searchRes.data.total || 0);
      setPage(1);
      if (searchRes.data.credits) setCredits(searchRes.data.credits);
      setExpandedId(null);
    } catch (err) {
      toast.error('AI search failed, using keyword search');
      handleSearch(1);
    } finally {
      setAiParsing(false);
      setLoading(false);
    }
  };

  const handleSearch = useCallback(async (pageNum = 1) => {
    const currentFilters = getCurrentFilters();
    if (!Object.keys(currentFilters).length) {
      toast.error('Add at least one filter', { description: 'Pick an industry, location, keyword or size — then hit Search.' });
      return;
    }
    setLoading(true);
    setHasSearched(true);
    try {
      const body = { ...currentFilters, page: pageNum, per_page: 25 };
      const res = await api.post('/api/leads/search/companies', body);
      if (res.data.error && res.data.message) {
        setProviderError(res.data.message);
        toast.error('Search unavailable', { description: res.data.message });
      } else {
        setProviderError(null);
      }
      setResults(res.data.organizations || []);
      setTotal(res.data.total || 0);
      setPage(pageNum);
      if (res.data.credits) setCredits(res.data.credits);
      setExpandedId(null);
    } catch (err) {
      toast.error('Search failed: ' + (err.response?.data?.detail || err.message));
    } finally { setLoading(false); }
  }, [searchName, selectedIndustries, selectedLocations, selectedEmployeeRanges, keywords, selectedRevenue, selectedFundingStages, dataFreshness]);

  const handleExpand = async (company) => {
    if (expandedId === company.apollo_id) { setExpandedId(null); return; }
    setExpandedId(company.apollo_id);

    if (!companyPeople[company.apollo_id]) {
      setLoadingPeopleFor(company.apollo_id);
      try {
        const peopleBody = { domain: company.domain, limit: 15 };
        if (seniorityFilter) peopleBody.seniorities = [seniorityFilter];
        const res = await api.post('/api/leads/search/people', peopleBody);
        setCompanyPeople(prev => ({ ...prev, [company.apollo_id]: res.data.contacts || [] }));
        if (res.data.credits) setCredits(prev => ({ ...prev, ...res.data.credits }));
      } catch { toast.error('Failed to load contacts'); }
      finally { setLoadingPeopleFor(null); }
    }
  };

  const handleReveal = async (person, revealPhone = false) => {
    setRevealingId(revealPhone ? `${person.apollo_id}-mobile` : person.apollo_id);
    try {
      const res = await api.post('/api/leads/search/reveal', {
        apollo_id: person.apollo_id, first_name: person.first_name, last_name: person.last_name,
        reveal_phone: revealPhone,
      });
      if (res.data.success && res.data.data) {
        const revealed = res.data.data;
        setCompanyPeople(prev => {
          const updated = { ...prev };
          for (const key of Object.keys(updated)) {
            updated[key] = updated[key].map(p =>
              p.apollo_id === person.apollo_id ? { ...p, email: revealed.email || p.email, phone: revealed.phone || p.phone, has_email: true } : p
            );
          }
          return updated;
        });
        if (res.data.credits) setCredits(prev => ({ ...prev, balance: res.data.credits.balance }));
        if (revealPhone) {
          toast.success('Mobile reveal requested', { description: 'The number arrives shortly (premium) and will appear automatically.' });
        } else {
          toast.success(`Revealed: ${revealed.email || 'contact'}`);
        }
      } else { toast.error(res.data.message || 'Reveal failed'); }
    } catch (err) {
      if (err.response?.status === 402) toast.error('Insufficient credits');
      else toast.error('Reveal failed');
    } finally { setRevealingId(null); }
  };

  const handleImport = async (company, person) => {
    const targetCompany = company || results.find(c => c.apollo_id === expandedId);
    if (!targetCompany) return;
    const importKey = person?.apollo_id || targetCompany.apollo_id;
    setImportingId(importKey);
    // Show an in-progress toast so the user knows their click was heard —
    // the import round-trip can take 20-30s while Apollo enriches + we score
    // the lead + build the cadence + queue Brain actions.
    const importToastId = toast.loading(
      person
        ? `Adding ${person.name || 'contact'} to pipeline — enriching + scoring...`
        : `Adding ${targetCompany.name} to pipeline — enriching + scoring...`,
      { duration: Infinity, description: "This usually takes 15-30s. You can keep browsing." }
    );
    try {
      const body = {
        company_name: targetCompany.name, domain: targetCompany.domain,
        industry: targetCompany.industry, employee_count: targetCompany.employee_count,
        city: targetCompany.city, state: targetCompany.state, country: targetCompany.country,
        website_url: targetCompany.website_url, linkedin_url: targetCompany.linkedin_url,
        apollo_id: targetCompany.apollo_id,
      };
      if (person) {
        body.people = [{
          name: person.name, first_name: person.first_name, last_name: person.last_name,
          title: person.title, email: person.email || '', phone: person.phone || '',
          linkedin_url: person.linkedin_url || '', apollo_id: person.apollo_id,
          city: person.city, state: person.state, country: person.country, seniority: person.seniority,
        }];
      }
      const res = await api.post('/api/leads/search/import', body);
      if (res.data.success) {
        const lead = res.data.leads?.[0];
        const scoreInfo = lead?.ai_score ? ` | Brain Score: ${lead.ai_score} (${lead.ai_qualification?.toUpperCase()})` : '';
        toast.success(`Imported ${res.data.imported_count} lead(s)${scoreInfo}`, {
          id: importToastId,
          action: lead?.id ? { label: 'View Lead', onClick: () => navigate(`/leads/${lead.id}`) } : undefined,
        });
      } else {
        toast.dismiss(importToastId);
      }
    } catch (err) {
      toast.error('Import failed: ' + (err.response?.data?.detail || err.message), { id: importToastId });
    } finally { setImportingId(null); }
  };

  const handleKeywordAdd = (e) => {
    if (e.key === 'Enter' && keywordInput.trim()) {
      setKeywords(prev => [...new Set([...prev, keywordInput.trim()])]); setKeywordInput('');
    }
  };
  const handleLocationAdd = (e) => {
    if (e.key === 'Enter' && locationInput.trim()) {
      // Title-case for Apollo compatibility: "india" → "India", "united states" → "United States"
      const normalized = locationInput.trim().replace(/\b\w/g, c => c.toUpperCase());
      setSelectedLocations(prev => [...new Set([...prev, normalized])]); setLocationInput('');
    }
  };
  const toggleIndustry = (ind) => setSelectedIndustries(prev => prev.includes(ind) ? prev.filter(i => i !== ind) : [...prev, ind]);
  const toggleEmployeeRange = (range) => setSelectedEmployeeRanges(prev => prev.includes(range) ? prev.filter(r => r !== range) : [...prev, range]);
  const toggleFundingStage = (stage) => setSelectedFundingStages(prev => prev.includes(stage) ? prev.filter(s => s !== stage) : [...prev, stage]);

  const activeFilterCount = selectedIndustries.length + selectedLocations.length + selectedEmployeeRanges.length + keywords.length + (seniorityFilter ? 1 : 0) + (selectedRevenue ? 1 : 0) + selectedFundingStages.length + (dataFreshness ? 1 : 0);
  const totalPages = Math.ceil(total / 25);

  /* ─── RENDER ─── */

  return (
    <div className="h-full flex flex-col" data-testid="lead-search-page">

      {/* ── HEADER ── */}
      <div className="px-6 py-4 border-b border-border/50 bg-background/95 backdrop-blur-sm sticky top-0 z-10">
        {/* Title Row */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-lg font-bold text-foreground flex items-center gap-2" data-testid="lead-search-title">
              <div className="p-1.5 rounded-lg bg-violet-500/10">
                <Sparkles className="h-4 w-4 text-violet-400" />
              </div>
              Discover Leads
            </h1>
            <p className="text-[11px] text-muted-foreground mt-0.5 ml-9">Search millions of companies. Surface revenue, tech stack, funding & contacts.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/15" data-testid="credit-balance-badge">
              <CreditCard className="h-3 w-3 text-amber-400" />
              <span className="text-[11px] font-semibold text-amber-400">${typeof credits.balance === 'number' ? credits.balance.toFixed(2) : '0.00'}</span>
            </div>
          </div>
        </div>

        {/* Go fully autonomous — master switch */}
        <div className="mb-3">
          <GoAutonomousCard />
        </div>

        {/* Phase 5.0 — Emergency Pause AI button (kill switch) */}
        <div className="mb-3">
          <PauseAiButton />
        </div>

        {/* Continuous auto-prospecting (opt-in) */}
        <div className="mb-3">
          <AutoProspectToggle />
        </div>

        {/* Autonomous inbound controls (Phase 2 + 3) */}
        <div className="mb-3">
          <AutonomyControls />
        </div>

        {/* Snoozed (deferred) leads — auto-resume schedule */}
        <div className="mb-3">
          <SnoozedLeadsCard />
        </div>

        {/* Search Bar + Actions */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            {aiParsing ? (
              <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-violet-400 animate-pulse" />
            ) : (
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/50" />
            )}
            <Input
              placeholder="Describe your ideal customer... e.g. 'SaaS companies in NYC with 500+ employees'"
              className="pl-9 pr-20 h-10 text-sm bg-muted/30 border-border/40 focus:border-violet-500/50 focus:ring-violet-500/20"
              value={searchName}
              onChange={e => setSearchName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAISearch()}
              data-testid="search-input"
              disabled={aiParsing}
            />
            {searchName.trim().length > 10 && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] text-violet-400/60 font-medium pointer-events-none">
                AI-powered
              </span>
            )}
          </div>

          <Button
            variant="outline" size="sm"
            className={`h-10 px-3 ${showFilters ? 'bg-violet-500/10 border-violet-500/30 text-violet-400' : 'border-border/40'}`}
            onClick={() => { setShowFilters(!showFilters); setShowSaved(false); }}
            data-testid="toggle-filters-btn"
          >
            <Filter className="h-3.5 w-3.5 mr-1.5" />Filters
            {activeFilterCount > 0 && (
              <span className="ml-1 w-4.5 h-4.5 rounded-full bg-violet-500 text-[9px] text-white flex items-center justify-center font-bold">{activeFilterCount}</span>
            )}
          </Button>

          <Button
            variant="outline" size="sm"
            className={`h-10 px-3 ${showSaved ? 'bg-violet-500/10 border-violet-500/30 text-violet-400' : 'border-border/40'}`}
            onClick={() => { setShowSaved(!showSaved); setShowFilters(false); }}
            data-testid="toggle-saved-btn"
          >
            <Bookmark className="h-3.5 w-3.5 mr-1.5" />Saved
            {savedSearches.length > 0 && (
              <span className="ml-1 w-4.5 h-4.5 rounded-full bg-foreground/70 text-[9px] text-background flex items-center justify-center font-bold">{savedSearches.length}</span>
            )}
          </Button>

          <Button
            className="h-10 px-5 bg-violet-600 hover:bg-violet-500 text-white font-medium"
            onClick={() => handleAISearch()}
            disabled={loading || aiParsing}
            data-testid="search-btn"
          >
            {loading || aiParsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-3.5 w-3.5 mr-1.5" />Search</>}
          </Button>
        </div>

        {/* Active Filter Chips */}
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap mt-2.5" data-testid="active-filters">
            {seniorityFilter && (
              <FilterChip label={SENIORITY_LABELS[seniorityFilter] || seniorityFilter} onRemove={() => setSeniorityFilter('')} />
            )}
            {selectedIndustries.map(ind => <FilterChip key={ind} label={ind} onRemove={() => toggleIndustry(ind)} />)}
            {selectedLocations.map(loc => <FilterChip key={loc} label={loc} onRemove={() => setSelectedLocations(prev => prev.filter(l => l !== loc))} />)}
            {selectedEmployeeRanges.map(r => <FilterChip key={r} label={`${EMPLOYEE_RANGES.find(er => er.value === r)?.label || r} emp`} onRemove={() => toggleEmployeeRange(r)} />)}
            {selectedRevenue && (
              <FilterChip label={`${REVENUE_RANGES.find(rr => rr.value === selectedRevenue)?.label || selectedRevenue} rev`} onRemove={() => setSelectedRevenue('')} />
            )}
            {selectedFundingStages.map(s => <FilterChip key={s} label={FUNDING_STAGES.find(fs => fs.value === s)?.label || s} onRemove={() => toggleFundingStage(s)} />)}
            {dataFreshness && (
              <FilterChip label={FRESHNESS_OPTIONS.find(o => o.value === dataFreshness)?.label || dataFreshness} onRemove={() => setDataFreshness('')} />
            )}
            {keywords.map(kw => <FilterChip key={kw} label={kw} onRemove={() => setKeywords(prev => prev.filter(k => k !== kw))} />)}

            <button className="text-[10px] text-red-400 hover:text-red-300 ml-1 transition-colors"
              onClick={() => { setSelectedIndustries([]); setSelectedLocations([]); setSelectedEmployeeRanges([]); setKeywords([]); setSeniorityFilter(''); setSelectedRevenue(''); setSelectedFundingStages([]); setDataFreshness(''); }}>
              Clear all
            </button>

            <span className="text-border/30 mx-0.5">|</span>

            {showSaveInput ? (
              <div className="flex items-center gap-1">
                <Input className="h-5 text-[10px] w-32 bg-muted/30" placeholder="Name this search..." value={saveName}
                  onChange={e => setSaveName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSaveSearch()} autoFocus data-testid="save-search-name-input" />
                <Button size="sm" variant="ghost" className="h-5 text-[10px] px-1.5" onClick={handleSaveSearch} data-testid="confirm-save-btn">Save</Button>
                <Button size="sm" variant="ghost" className="h-5 text-[10px] px-1.5" onClick={() => setShowSaveInput(false)}>Cancel</Button>
              </div>
            ) : (
              <button className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5 transition-colors" onClick={() => setShowSaveInput(true)} data-testid="save-search-trigger">
                <Bookmark className="h-2.5 w-2.5" />Save
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── FILTER PANEL ── */}
      {showFilters && (
        <div className="px-6 py-4 border-b border-border/30 bg-muted/10 space-y-4 animate-[fadeUp_0.15s_ease-out]" data-testid="filter-panel">
          {/* Industry */}
          <div>
            <h4 className="text-[11px] font-semibold text-foreground/80 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Briefcase className="h-3 w-3 text-muted-foreground" />Industry
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {INDUSTRY_OPTIONS.map(ind => (
                <button key={ind} onClick={() => toggleIndustry(ind)}
                  className={`text-[11px] px-2.5 py-1 rounded-md border transition-all ${
                    selectedIndustries.includes(ind)
                      ? 'bg-violet-500/15 text-violet-400 border-violet-500/30'
                      : 'border-border/40 text-foreground/50 hover:border-foreground/30 hover:text-foreground/70'
                  }`}
                  data-testid={`industry-${ind.replace(/\s+/g, '-')}`}
                >{ind}</button>
              ))}
            </div>
          </div>

          {/* Employee Size */}
          <div>
            <h4 className="text-[11px] font-semibold text-foreground/80 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Users className="h-3 w-3 text-muted-foreground" />Company Size
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {EMPLOYEE_RANGES.map(range => (
                <button key={range.value} onClick={() => toggleEmployeeRange(range.value)}
                  className={`text-[11px] px-2.5 py-1 rounded-md border transition-all ${
                    selectedEmployeeRanges.includes(range.value)
                      ? 'bg-violet-500/15 text-violet-400 border-violet-500/30'
                      : 'border-border/40 text-foreground/50 hover:border-foreground/30 hover:text-foreground/70'
                  }`}
                  data-testid={`emp-range-${range.label}`}
                >{range.label}</button>
              ))}
            </div>
          </div>

          {/* Revenue + Funding Stage + Data Freshness — Horizontal Row */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <h4 className="text-[11px] font-semibold text-foreground/80 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <DollarSign className="h-3 w-3 text-muted-foreground" />Annual Revenue
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {REVENUE_RANGES.map(r => (
                  <button key={r.value} onClick={() => setSelectedRevenue(selectedRevenue === r.value ? '' : r.value)}
                    className={`text-[11px] px-2.5 py-1 rounded-md border transition-all ${
                      selectedRevenue === r.value
                        ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                        : 'border-border/40 text-foreground/50 hover:border-foreground/30 hover:text-foreground/70'
                    }`}
                    data-testid={`revenue-${r.label}`}
                  >{r.label}</button>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-[11px] font-semibold text-foreground/80 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Layers className="h-3 w-3 text-muted-foreground" />Funding Stage
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {FUNDING_STAGES.map(s => (
                  <button key={s.value} onClick={() => toggleFundingStage(s.value)}
                    className={`text-[11px] px-2.5 py-1 rounded-md border transition-all ${
                      selectedFundingStages.includes(s.value)
                        ? 'bg-blue-500/15 text-blue-400 border-blue-500/30'
                        : 'border-border/40 text-foreground/50 hover:border-foreground/30 hover:text-foreground/70'
                    }`}
                    data-testid={`funding-${s.value}`}
                  >{s.label}</button>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-[11px] font-semibold text-foreground/80 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Clock className="h-3 w-3 text-muted-foreground" />Data Freshness
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {FRESHNESS_OPTIONS.map(o => (
                  <button key={o.value || 'any'} onClick={() => setDataFreshness(o.value)}
                    className={`text-[11px] px-2.5 py-1 rounded-md border transition-all ${
                      dataFreshness === o.value
                        ? 'bg-violet-500/15 text-violet-400 border-violet-500/30'
                        : 'border-border/40 text-foreground/50 hover:border-foreground/30 hover:text-foreground/70'
                    }`}
                    data-testid={`freshness-${o.value || 'any'}`}
                  >{o.label}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Seniority + Location + Keywords — Horizontal Row */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <h4 className="text-[11px] font-semibold text-foreground/80 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <UserCheck className="h-3 w-3 text-muted-foreground" />Seniority
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(SENIORITY_LABELS).map(([key, label]) => (
                  <button key={key} onClick={() => setSeniorityFilter(seniorityFilter === key ? '' : key)}
                    className={`text-[11px] px-2.5 py-1 rounded-md border transition-all ${
                      seniorityFilter === key
                        ? 'bg-violet-500/15 text-violet-400 border-violet-500/30'
                        : 'border-border/40 text-foreground/50 hover:border-foreground/30 hover:text-foreground/70'
                    }`}
                    data-testid={`seniority-${key}`}
                  >{label}</button>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-[11px] font-semibold text-foreground/80 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <MapPin className="h-3 w-3 text-muted-foreground" />Location
              </h4>
              <Input placeholder="City, state, or country + Enter" className="h-8 text-xs bg-background border-border/40" value={locationInput}
                onChange={e => setLocationInput(e.target.value)} onKeyDown={handleLocationAdd} data-testid="location-filter-input" />
              {selectedLocations.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {selectedLocations.map(loc => <FilterChip key={loc} label={loc} onRemove={() => setSelectedLocations(prev => prev.filter(l => l !== loc))} />)}
                </div>
              )}
            </div>
            <div>
              <h4 className="text-[11px] font-semibold text-foreground/80 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Code className="h-3 w-3 text-muted-foreground" />Keywords
              </h4>
              <Input placeholder="Keyword + Enter" className="h-8 text-xs bg-background border-border/40" value={keywordInput}
                onChange={e => setKeywordInput(e.target.value)} onKeyDown={handleKeywordAdd} data-testid="keyword-filter-input" />
              {keywords.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {keywords.map(kw => <FilterChip key={kw} label={kw} onRemove={() => setKeywords(prev => prev.filter(k => k !== kw))} />)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── SAVED SEARCHES PANEL ── */}
      {showSaved && (
        <div className="px-6 py-4 border-b border-border/30 bg-muted/10" data-testid="saved-searches-panel">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-foreground flex items-center gap-1.5"><BookmarkCheck className="h-3.5 w-3.5 text-violet-400" />Saved Searches</h3>
            <span className="text-[10px] text-muted-foreground">{savedSearches.length} saved</span>
          </div>
          <SavedSearchPanel items={savedSearches} onLoad={handleLoadSavedSearch} onDelete={handleDeleteSavedSearch} onRun={handleRunSavedSearch} runningId={runningSavedId} />
        </div>
      )}

      {/* ── ICP SUGGESTION BANNER ── */}
      {!hasSearched && suggestions?.message && (
        <div className="mx-6 mt-4 px-4 py-3 rounded-xl bg-violet-500/5 border border-violet-500/15" data-testid="suggestion-banner">
          <div className="flex items-start gap-2.5">
            <div className="p-1.5 rounded-lg bg-violet-500/10 shrink-0 mt-0.5">
              <Zap className="h-3.5 w-3.5 text-violet-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-violet-300">{suggestions.message}</p>
              {suggestions.keywords?.length > 0 && (
                <div className="flex items-center gap-1.5 mt-1.5">
                  <span className="text-[10px] text-muted-foreground">Suggested:</span>
                  {suggestions.keywords.map((kw, i) => (
                    <button key={i} className="text-[10px] px-2 py-0.5 rounded-md bg-violet-500/10 text-violet-400 border border-violet-500/20 hover:bg-violet-500/20 transition-colors"
                      onClick={() => setKeywords(prev => [...new Set([...prev, kw])])}>+ {kw}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── RESULTS ── */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          /* Loading Skeleton */
          <div className="px-6 py-4 space-y-3" data-testid="search-loading-skeleton">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="rounded-xl border border-border/30 p-4 space-y-2.5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted/40 animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-48 bg-muted/40 rounded animate-pulse" />
                    <div className="flex gap-2">
                      <div className="h-3 w-20 bg-muted/30 rounded animate-pulse" />
                      <div className="h-3 w-16 bg-muted/30 rounded animate-pulse" />
                      <div className="h-3 w-24 bg-muted/30 rounded animate-pulse" />
                    </div>
                    <div className="h-3 w-full bg-muted/20 rounded animate-pulse" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : !hasSearched ? (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-16 text-center px-6" data-testid="empty-state">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500/10 to-indigo-500/10 border border-violet-500/10 flex items-center justify-center mb-5">
              <Search className="h-8 w-8 text-violet-400/60" />
            </div>
            <h2 className="text-base font-semibold text-foreground mb-1.5">Find your next customers</h2>
            <p className="text-xs text-muted-foreground max-w-lg leading-relaxed">
              Search by company name, industry, location, and size. Each result shows revenue, tech stack, funding stage, and contacts — with one-click add to your pipeline.
            </p>

            {/* Quick Start Buttons */}
            {suggestions?.industries?.length > 0 && (
              <div className="mt-6">
                <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider mb-2">Quick start from your ICP</p>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {suggestions.industries.slice(0, 4).map((ind, i) => (
                    <button key={i}
                      className="text-[11px] px-3 py-1.5 rounded-lg bg-muted/30 border border-border/40 text-foreground/60 hover:text-foreground hover:border-violet-500/30 hover:bg-violet-500/5 transition-all flex items-center gap-1"
                      onClick={() => { setSelectedIndustries([ind]); handleSearch(1); }}
                    >
                      <ArrowRight className="h-3 w-3" />{ind}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {savedSearches.length > 0 && (
              <div className="mt-5">
                <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider mb-2">Or run a saved search</p>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {savedSearches.slice(0, 3).map(s => (
                    <button key={s.id}
                      className="text-[11px] px-3 py-1.5 rounded-lg bg-muted/30 border border-border/40 text-foreground/60 hover:text-foreground hover:border-violet-500/30 hover:bg-violet-500/5 transition-all flex items-center gap-1"
                      onClick={() => handleRunSavedSearch(s.id)}
                    >
                      <BookmarkCheck className="h-3 w-3 text-violet-400" />{s.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : results.length === 0 ? (
          /* No Results */
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-xl bg-muted/20 flex items-center justify-center mb-4">
              <Search className="h-6 w-6 text-muted-foreground/40" />
            </div>
            <p className="text-sm text-muted-foreground" data-testid="discover-empty-state">
              {providerError || 'No companies found. Try broader filters.'}
            </p>
          </div>
        ) : (
          /* Results Grid */
          <>
            <div className="px-6 py-2.5 flex items-center justify-between bg-muted/10 border-b border-border/20">
              <span className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">{total.toLocaleString()}</span> companies found
              </span>
              <span className="text-[10px] text-muted-foreground/60">
                Enrichment: ${credits.enrich_cost}/co  |  Reveal: ${credits.reveal_cost}/contact
              </span>
            </div>

            <div className="px-6 py-3 space-y-2" data-testid="results-list">
              {results.map(company => (
                <React.Fragment key={company.apollo_id}>
                  <CompanyCard
                    company={company}
                    expanded={expandedId === company.apollo_id}
                    onExpand={handleExpand}
                    onImport={handleImport}
                    importingId={importingId}
                  />
                  {expandedId === company.apollo_id && (
                    <div className="ml-0 -mt-1 rounded-b-xl border border-t-0 border-violet-500/20 bg-violet-500/[0.02]">
                      <CompanyPeoplePanel
                        company={company}
                        people={companyPeople[company.apollo_id] || []}
                        loadingPeople={loadingPeopleFor === company.apollo_id}
                        onReveal={handleReveal}
                        revealingId={revealingId}
                        onImport={handleImport}
                        importingId={importingId}
                        credits={credits}
                        seniorityFilter={seniorityFilter}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 py-4 border-t border-border/20">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => handleSearch(page - 1)} data-testid="prev-page-btn" className="border-border/40">Previous</Button>
                <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => handleSearch(page + 1)} data-testid="next-page-btn" className="border-border/40">Next</Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
